<template>
  <div>
    <b-table
      responsive
      small
      show-empty
      :items="rows"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filter"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-compare="sortCompare"
      @filtered="onFiltered"
      @row-selected="onRowSelected"
    >
      <template #cell(source)="{ item }">
        <b-badge pill variant="secondary">{{ sourceLabel(item.sourceType) }}</b-badge>
      </template>

      <template #cell(asset)="{ item }">
        <template v-if="item.sourceType === 'network'">
          <span>{{ item.raw.ip }}</span>
        </template>

        <template v-else-if="item.sourceType === 'endpointThreats'">
          <b-link :to="`/xtends/${item.raw.id}`">{{ item.raw.hostname }}</b-link>
        </template>

        <template v-else>
          <template v-if="Array.isArray(item.raw.xtendEntities) && item.raw.xtendEntities.length">
            <span v-for="(entity, idx) in item.raw.xtendEntities" :key="entity.id || idx">
              <b-link v-if="entity.id" :to="`/xtends/${entity.id}`">{{ entity.name }}</b-link>
              <span v-else>{{ entity.name }}</span>
              <span v-if="idx < item.raw.xtendEntities.length - 1">, </span>
            </span>
          </template>
          <span v-else class="text-muted">—</span>
        </template>
      </template>

      <template #cell(threatInfo)="{ item, toggleDetails }">
        <template v-if="item.sourceType === 'network' || item.sourceType === 'endpointThreats'">
          <b-row>
            <b-col>{{ item.raw.events.join(', ') }}</b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-button
                variant="flat-primary"
                class="p-0"
                @click="showRowDetail({ item, toggleDetails }, 'alerts')"
              >
                {{ item.raw.count }} record(s)
              </b-button>
            </b-col>
          </b-row>
        </template>

        <template v-else>
          <div>{{ item.raw.msg || item.raw.message || '—' }}</div>
          <div class="text-muted">{{ item.raw.type || item.raw.category || '' }}</div>
        </template>
      </template>

      <template #cell(severity)="row">
        <b-badge pill :variant="alertStatusVariant(row.value)">
          {{ alertStatusText(row.value) }}
        </b-badge>
      </template>

      <template #cell(time)="{ item }">
        <span v-if="item.time">{{ formatDate(item.time) }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(actions)="{ item, toggleDetails }">
        <div class="d-flex align-items-center">
          <template v-if="item.sourceType === 'network' || item.sourceType === 'endpointThreats'">
            <b-icon
              icon="stars"
              class="cursor-pointer text-primary"
              title="AI Suggestions"
              @click="showRowDetail({ item, toggleDetails }, 'ai')"
            />
            <b-icon
              icon="box-arrow-up-right"
              class="cursor-pointer text-primary ml-2"
              title="ATT&CK Navigator"
              @click="openNavigator(item)"
            />
            <b-icon
              icon="eye"
              class="cursor-pointer text-primary ml-2"
              title="Show Evidence"
              @click="showRowDetail({ item, toggleDetails }, 'events')"
            />
          </template>
        </div>
      </template>

      <template #row-details="{ item, toggleDetails }">
        <div class="p-2">
          <template v-if="item.sourceType === 'network'">
            <ThreatAlertsTable
              v-if="detailMode[item.rowKey] === 'alerts'"
              :alerts="item.raw.alerts"
              @close="() => closeDetail({ item, toggleDetails })"
            />
            <ThreatEventsTable
              v-else-if="detailMode[item.rowKey] === 'events'"
              :item="item.raw"
              @close="() => closeDetail({ item, toggleDetails })"
            />
            <AlertSuggestionsContent
              v-else-if="detailMode[item.rowKey] === 'ai'"
              :alertAggkey="firstAlertId(item.raw)"
              @close="() => closeDetail({ item, toggleDetails })"
            />
          </template>
          <template v-else-if="item.sourceType === 'endpointThreats'">
            <ThreatAlertsTable
              v-if="detailMode[item.rowKey] === 'alerts'"
              :alerts="item.raw.alerts"
              @close="() => closeDetail({ item, toggleDetails })"
            />
            <EndpointThreatEventsTable
              v-else-if="detailMode[item.rowKey] === 'events'"
              :alerts="item.raw.alerts"
              @close="() => closeDetail({ item, toggleDetails })"
            />
            <AlertSuggestionsContent
              v-else-if="detailMode[item.rowKey] === 'ai'"
              :alertAggkey="item.raw.hostname"
              @close="() => closeDetail({ item, toggleDetails })"
            />
          </template>
          <template v-else>
            <div class="text-muted">Select a row to view details.</div>
          </template>
        </div>
      </template>

      <template #empty>
        <div class="text-center text-muted py-3">No results</div>
      </template>
    </b-table>

    <div class="d-flex justify-content-end mt-2">
      <b-pagination
        v-model="currentPage"
        :total-rows="paginationTotalRows"
        :per-page="perPage"
        first-number
        last-number
        size="sm"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import SecurityEventBus from '@/xvisor/bus/SecurityEventBus';
import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';
import ThreatAlertsTable from '@/xvisor/components/security/ThreatAlertsTable.vue';
import ThreatEventsTable from '@/xvisor/components/security/ThreatEventsTable.vue';
import EndpointThreatEventsTable from '@/xvisor/components/security/EndpointThreatEventsTable.vue';
import AlertSuggestionsContent from '@/xvisor/components/app/AlertSuggestionsContent.vue';
import alertSeverities from '@/xvisor/constants/alertSeverities';

export default {
  name: 'ThreatsAllTable',
  components: {
    ThreatAlertsTable,
    ThreatEventsTable,
    EndpointThreatEventsTable,
    AlertSuggestionsContent,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    filter: {
      type: String,
      default: '',
    },
    perPage: {
      type: Number,
      default: 20,
    },
    networkItems: {
      type: Array,
      default: null,
    },
    endpointAlertItems: {
      type: Array,
      default: null,
    },
    endpointThreatItems: {
      type: Array,
      default: null,
    },
    navigatorConfig: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      rows: [],
      currentPage: 1,
      filteredLength: null,
      sortBy: 'severity',
      sortDesc: false,
      detailMode: Object.create(null),
    };
  },
  computed: {
    paginationTotalRows() {
      return this.filteredLength !== null ? this.filteredLength : this.rows.length;
    },
    fields() {
      const mk = (key, label, sortable = false) => ({
        key,
        label,
        sortable,
        thStyle: this.colStyle(key),
        tdAttr: () => ({ style: this.colStyle(key) }),
      });
      return [
        mk('asset', 'Entity', true),
        mk('threatInfo', 'Threat Info', false),
        mk('time', 'Time', true),
        mk('source', 'Source', true),
        mk('severity', 'Severity', true),
        mk('actions', 'Actions', false),
      ];
    },
  },
  watch: {
    networkItems: { immediate: true, handler() { this.rebuildRows(); } },
    endpointAlertItems: { immediate: true, handler() { this.rebuildRows(); } },
    endpointThreatItems: { immediate: true, handler() { this.rebuildRows(); } },
    timeRange() {
      this.currentPage = 1;
      this.filteredLength = null;
      this.detailMode = Object.create(null);
    },
  },
  methods: {
    alertStatusVariant(status) {
      if (status === alertSeverities.critical) return 'danger';
      if (status === alertSeverities.warning) return 'warning';
      return 'success';
    },
    alertStatusText(status) {
      if (status === alertSeverities.critical) return 'Critical';
      if (status === alertSeverities.warning) return 'Warning';
      return 'Notice';
    },
    sourceLabel(sourceType) {
      switch (sourceType) {
        case 'network': return 'Network';
        case 'endpointAlerts': return 'Endpoint Deterministics';
        case 'endpointThreats': return 'Endpoint Threats';
        default: return 'Unknown';
      }
    },
    formatDate(dateStr) {
      return moment(dateStr).format(momentTimeFormat.dateTime);
    },
    firstAlertId(raw) {
      const a = raw?.alerts?.[0];
      return a?.id || null;
    },
    sortCompare(aRow, bRow, key) {
      const a = this.sortValue(aRow, key);
      const b = this.sortValue(bRow, key);

      if (typeof a === 'number' && typeof b === 'number') return a - b;
      if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();

      const as = (a ?? '').toString().toLowerCase();
      const bs = (b ?? '').toString().toLowerCase();
      if (as < bs) return -1;
      if (as > bs) return 1;
      return 0;
    },
    sortValue(row, key) {
      if (key === 'severity') return row.severity ?? -1;
      if (key === 'time') return row.time ? new Date(row.time) : new Date(0);
      if (key === 'asset') return row.assetKey ?? '';
      if (key === 'source') return row.sourceType ?? '';
      return row[key];
    },
    onFiltered(filteredItems) {
      this.filteredLength = filteredItems.length;
      this.currentPage = 1;
      this.$emit('filtered-length', filteredItems.length);
    },
    onRowSelected(selected) {
      const first = Array.isArray(selected) ? selected[0] : null;
      if (!first) return;

      if (first.sourceType === 'endpointAlerts') {
        SecurityEventBus.$emit('update-selected-alert', first.raw);
      }
    },
    rebuildRows() {
      const network = Array.isArray(this.networkItems) ? this.networkItems : [];
      const endpointAlerts = Array.isArray(this.endpointAlertItems) ? this.endpointAlertItems : [];
      const endpointThreats = Array.isArray(this.endpointThreatItems) ? this.endpointThreatItems : [];

      const networkRows = network.map((item) => {
        const {
          ip,
          severity,
          time,
          lastSeen,
          timestamp,
          ...rest
        } = item;
        const resolvedTime = time || lastSeen || timestamp || null;
        const assetKey = ip || '';

        return {
          navigatorId: ip,
          sourceType: 'network',
          rowKey: `network:${assetKey}:${resolvedTime || ''}`,
          time: resolvedTime,
          severity,
          assetKey,
          detailsLink: assetKey ? `/alerts/ip/${assetKey}` : null,
          raw: {
            ip,
            severity,
            time,
            lastSeen,
            timestamp,
            ...rest,
          },
        };
      });

      const endpointAlertRows = endpointAlerts.map((item) => {
        const {
          id, alertId, severity, time, ts, timestamp, createdAt, xtendEntities, ...rest
        } = item;

        const resolvedTime = time || ts || timestamp || createdAt || null;
        const assetKey = this.endpointEntitiesKey({ xtendEntities });
        const rowId = id || alertId || assetKey;

        return {
          sourceType: 'endpointAlerts',
          rowKey: `endpointAlerts:${rowId}:${resolvedTime || ''}`,
          time: resolvedTime,
          severity,
          assetKey,
          detailsLink: null,
          raw: {
            id,
            alertId,
            severity,
            time,
            ts,
            timestamp,
            createdAt,
            xtendEntities,
            ...rest,
          },
        };
      });

      const endpointThreatRows = endpointThreats.map((item) => {
        const {
          id, hostname, device, endpoint, severity, time, lastSeen, timestamp, ...rest
        } = item;

        const resolvedTime = time || lastSeen || timestamp || null;
        const assetKey = hostname || device || endpoint || '';

        return {
          sourceType: 'endpointThreats',
          rowKey: `endpointThreats:${assetKey}:${resolvedTime || ''}`,
          time: resolvedTime,
          severity,
          assetKey,
          detailsLink: assetKey ? `/security/endpoint/threats/${assetKey}` : null,
          raw: {
            id,
            hostname,
            device,
            endpoint,
            severity,
            time,
            lastSeen,
            timestamp,
            ...rest,
          },
        };
      });

      const out = [...networkRows, ...endpointAlertRows, ...endpointThreatRows]
        .slice()
        .sort((a, b) => (b.severity ?? -1) - (a.severity ?? -1));

      this.rows = out;
      this.$emit('total-rows', this.rows.length);
    },
    endpointEntitiesKey(item) {
      const ents = Array.isArray(item?.xtendEntities) ? item.xtendEntities : [];
      return ents
        .map((e) => e.id || e.name || '')
        .filter(Boolean)
        .join('|');
    },
    showRowDetail({ item, toggleDetails }, mode) {
      if (!item?.rowKey) return;

      const current = this.detailMode[item.rowKey] || null;
      const nextMode = current === mode ? null : mode;

      if (nextMode) this.$set(this.detailMode, item.rowKey, nextMode);
      else this.$delete(this.detailMode, item.rowKey);

      toggleDetails();
    },
    closeDetail({ item, toggleDetails }) {
      if (!item?.rowKey) return;
      if (this.detailMode[item.rowKey]) this.$delete(this.detailMode, item.rowKey);
      toggleDetails();
    },
    colStyle(fieldKey) {
      const widths = {
        source: { width: '10%', minWidth: '100px' },
        asset: { width: '15%', minWidth: '150px' },
        threatInfo: { width: '50%', minWidth: '500px' },
        time: { width: '10%', minWidth: '100px' },
        severity: { width: '5%', minWidth: '50px' },
        actions: { width: '10%', minWidth: '100px' },
      };

      const { width, minWidth } = widths[fieldKey] || { width: '150px', minWidth: '50px' };
      return { width, minWidth };
    },
    navigatorLayerUrlForRow(row) {
      const start = this.timeRange.start.toISOString();
      const end = this.timeRange.end.toISOString();

      if (row.sourceType === 'network') {
        const id = row.raw?.id;
        if (!id) return null;
        return `${this.navigatorConfig.networkLayerBase}${id}.json?start=${start}&end=${end}`;
      }

      if (row.sourceType === 'endpointThreats') {
        const id = row.raw?.hostname;
        if (!id) return null;
        return `${this.navigatorConfig.endpointLayerBase}${id}.json?start=${start}&end=${end}`;
      }

      return null;
    },
    calculateNavigatorLink(row) {
      const layerUrl = this.navigatorLayerUrlForRow(row);
      if (!layerUrl) return null;
      return `${this.navigatorConfig.baseUrl}${encodeURIComponent(layerUrl)}`;
    },
    openNavigator(row) {
      const url = this.calculateNavigatorLink(row);
      if (!url) return;
      window.open(url, '_blank');
    },
  },
};
</script>
